import { Fragment } from 'react'
import { anzahlwort, t } from './sprache'
import type { Sprachschluessel } from './sprache'
import type { Importvorschau, Importzeile } from './typen'

/**
 * Der gesperrte Übernehmen-Knopf nennt den wahren Grund und führt hin
 * (5.1 Abschnitt 3.41, R935).
 *
 * **Der Befund vom 2026-10-03:** Neben dem gesperrten Knopf stand *„2 Zeilen
 * sind keinem Konzernkonto zugeordnet"* — beide Zeilen hatten ihr
 * Konzernkonto. Offen waren sie, weil die Datei keinen Partner nannte. Der
 * Satz kannte nur einen Grund und nannte ihn für jeden; einen Knopf gab es
 * nur, wenn ein Konto oder eine Bewegungsart fehlte.
 *
 * **Drei Arten, und die dritte fängt alles auf:** Eine Zeile ohne
 * Konzernkonto und eine Zeile mit offener Zuordnung haben ihren Handgriff im
 * Zuordnungsfenster. Jede andere gesperrte Zeile trägt einen Befund in ihrer
 * letzten Spalte — für sie gibt es den Knopf, der nur diese Zeilen zeigt. Ein
 * neuer Abweisungsgrund im Rechenkern landet damit von selbst in der dritten
 * Art, statt unter einem falschen Satz zu stehen.
 */
export type Sperrart = 'konto' | 'zuordnung' | 'befund'

/** In der Reihenfolge, in der sie neben dem Knopf stehen. */
export const SPERRARTEN: readonly Sperrart[] = ['konto', 'zuordnung', 'befund']

/** Warum diese Zeile die Übernahme aufhält — `null`, wenn sie es nicht tut. */
export function sperrart(z: Importzeile): Sperrart | null {
  if (z.status === 'gueltig') {
    return null
  }

  if (z.konto === null && z.lokalnummer.length > 0) {
    return 'konto'
  }

  // „Offen" ist, was einen Handgriff im Zuordnungsfenster hat (R595).
  return z.status === 'offen' ? 'zuordnung' : 'befund'
}

/** Je Art die Zahl der Zeilen — nur Arten, die vorkommen. */
export function sperrgruende(vorschau: Importvorschau): { art: Sperrart; zeilen: number }[] {
  return SPERRARTEN.map((art) => ({
    art,
    zeilen: vorschau.zeilen.filter((z) => sperrart(z) === art).length,
  })).filter((g) => g.zeilen > 0)
}

export function Sperrgruende({
  vorschau,
  nurBefunde,
  aufNurBefunde,
}: {
  vorschau: Importvorschau
  /** Ob die Tabelle gerade nur die Zeilen mit Befund zeigt. */
  nurBefunde: boolean
  aufNurBefunde: (nur: boolean) => void
}) {
  const gruende = sperrgruende(vorschau)

  return (
    <span className="erlaeuterung">
      {' '}
      {t('import.sperre.nochnicht')}{' '}
      {gruende.map((g, i) => (
        <Fragment key={g.art}>
          {i > 0 && ' · '}
          {t(`import.sperre.${g.art}.${g.zeilen === 1 ? 'eins' : 'mehrere'}` as Sprachschluessel, {
            anzahl: anzahlwort(g.zeilen),
          })}
          {/*
            Konto und Zuordnung haben ihren Knopf links neben *Übernehmen*
            (R589, R609). Der Befund hatte keinen — er bekommt ihn hier.
          */}
          {g.art === 'befund' && (
            <>
              {' '}
              <button type="button" className="schmal" onClick={() => aufNurBefunde(!nurBefunde)}>
                {t(nurBefunde ? 'import.sperre.allezeigen' : 'import.sperre.zeilenzeigen')}
              </button>
            </>
          )}
        </Fragment>
      ))}
      {gruende.length > 0 ? '. ' : ''}
      {t('import.sperre.schluss')}
    </span>
  )
}
